import React, { useEffect, useState, useMemo, useCallback, useRef  } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AssetPreviewCard from './AssetPreviewCard';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import { useTranslation } from 'react-i18next'; 
import { exportToExcel } from './exportUtils';
import { Briefcase, CheckSquare, Square, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';
import StatusBadge from './StatusBadge';
import ListPageShell from './list/ListPageShell';
import SortableTh from './list/SortableTh';
import { useFavoriteFilters } from './useFavoriteFilters';
import { useAuthorizedSites } from './useAuthorizedSites';

const WorkOrderList = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const [totalRecords, setTotalRecords] = useState(0);
  const [showAllSites, setShowAllSites] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [view, setView] = useState("");
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [filters, setFilters] = useState({
    wonum: "", description: "", status: "", wo_type: "", assetnum: "", supervisor: "", location_id: "", targstartdate: "", site_id: ""
  });
  const { saveFavorite } = useFavoriteFilters('WORKORDER');
  const handleSaveFavoriteFilter = async () => {
    const { value: name } = await Swal.fire({
      ...swalTheme(),
      title: t('favorites.namePrompt', 'Name this favorite filter'),
      input: 'text',
      inputPlaceholder: t('favorites.namePlaceholder', 'e.g. My Open WOs'),
      showCancelButton: true,
      confirmButtonText: t('common.save', 'Save'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      inputValidator: (value) => !value?.trim() && t('favorites.nameRequired', 'Please enter a name')
    });
    // 🎯 مش بس فلاتر الأعمدة - لازم نحفظ كمان الكويري السريعة (searchTerm)
    // والبارامتر العام all_sites، وإلا لو المفضلة دي فعلاً معتمدة على
    // "شوف كل المواقع" مثلاً، تطبيقها تاني هيرجّعها لموقعك بس من غير قصد
    if (name && name.trim()) saveFavorite(name, { ...filters, searchTerm, all_sites: showAllSites });
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'wonum', direction: 'desc' });
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [searchParams, setSearchParams] = useSearchParams();
  
  // 🎯 استخراج الـ view السريع من الرابط (عشان القائمة الجانبية)
  const activeView = searchParams.get('view') || '';

  const [hoveredAsset, setHoveredAsset] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const previewTimerRef = useRef(null);
  
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const token = localStorage.getItem('token');
  const { canCreate, canUpdate, canDelete } = usePermissions('workorders');
  // 🎯 "كل المواقع" متاحة لأي حد عنده أكتر من موقع مسموح (السيكيورتي جروب/شاشة
  // اليوزر)، والباك إند بيعرض له مواقعه المسموحة بس
  const { multiSite } = useAuthorizedSites();
  const canMultiSite = session.role?.name === 'SUPER_ADMIN' || multiSite;

  // 🎯 دالة تنفيض الشاشة بالكامل
  const handleClearFilters = () => {
    setSearchParams({}); // بتمسح كل الفلاتر من الرابط (URL)
    setSearchTerm("");   // بتفضي مربع البحث السريع
    setSelectedIds([]);  // بتلغي تحديد أي صفوف (Checkboxes)
  };

  // 🎯 باجينج كلاسيكي بصفحات (زي AssetsList) بدل الخلط القديم بين infinite-scroll
  // و jumpToPage اللي كانا بيتصارعوا على نفس الـ state.
  const fetchWorkOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { site_id: filterSiteValue, ...otherFilters } = filters;

      const res = await axios.get('/api/workorders', {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          all_sites: showAllSites,
          site_id: showAllSites ? undefined : sessionSite,
          org_id: showAllSites ? undefined : sessionOrg,
          searchTerm: searchTerm,
          filter_site: filterSiteValue,
          view: activeView, // 🎯 تمرير فلتر القائمة الجانبية للباك إند
          ...otherFilters
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setWorkOrders(res.data.data || []);
      setTotalRecords(res.data.total || 0);
      setSelectedIds([]);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error(t('wo.messages.sessionExpired'));
      } else {
        toast.error(t('wo.messages.fetchError'));
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, sessionSite, sessionOrg, showAllSites, searchTerm, filters, activeView, token, t]);

  // 🎯 أي تغيير في البحث/الفلاتر/النطاق يرجّعنا لصفحة 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, JSON.stringify(filters), showAllSites, activeView]);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  useEffect(() => {
    const openAllSites = () => { if (canMultiSite) setShowAllSites(true); };
    const viewMySite = () => setShowAllSites(false);
    const wapprView = () => {
        setSearchParams(prev => {
            prev.set('view', 'WAPPR');
            prev.set('page', '1'); // بنصفر الصفحة عشان لو هو في صفحة 5 ومفيش غير 10 نتايج مايطلعش شاشة فاضية
            return prev;
        });
    };

    const inprgView = () => {
        setSearchParams(prev => {
            prev.set('view', 'INPRG');
            prev.set('page', '1');
            return prev;
        });
    };

    const compTodayView = () => {
        setSearchParams(prev => {
            prev.set('view', 'COMP_TODAY');
            prev.set('page', '1');
            return prev;
        });
    };

    const clearView = () => {
        setSearchParams(prev => {
            prev.delete('view'); // لو داس على "كل الأوامر" بنشيل الفلتر خالص
            prev.set('page', '1');
            return prev;
        });
    };
    
    window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
    window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
    window.addEventListener('GLOBAL_WAPPR', wapprView);
    window.addEventListener('GLOBAL_VIEW_INPRG', inprgView);
    window.addEventListener('GLOBAL_VIEW_COMP_TODAY', compTodayView);
    window.addEventListener('GLOBAL_VIEW_ALL_WOS', clearView);
    return () => {
      window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      window.removeEventListener('GLOBAL_WAPPR', wapprView);
      window.removeEventListener('GLOBAL_VIEW_INPRG', inprgView);
      window.removeEventListener('GLOBAL_VIEW_COMP_TODAY', compTodayView);
      window.removeEventListener('GLOBAL_VIEW_ALL_WOS', clearView);
    };
  }, [setSearchParams, canMultiSite]);

  // 🎯 تطبيق فلتر مفضّل - الزرار نفسه جوه السايدبار (AppSidebar.js) اللي
  // مالوش وصول مباشر لحالة الفلاتر هنا، فبيبعت الفلتر المختار عن طريق
  // نفس أسلوب الأحداث العامة المستخدم لباقي أزرار السايدبار
  useEffect(() => {
    const applyFavorite = (e) => {
      if (e.detail?.appName === 'WORKORDER' && e.detail?.filters) {
        // 🎯 المفضلة بتحمل searchTerm/all_sites جنب فلاتر الأعمدة نفسها -
        // نفصلهم عن بعض ونوجه كل واحد للـ state بتاعه (مفضلات قديمة اتحفظت
        // قبل الإضافة دي مش هتلاقي المفتاحين فييجي undefined ونتجاهلهم بأمان)
        const { searchTerm: favSearchTerm, all_sites: favAllSites, ...favFilters } = e.detail.filters;
        setFilters(prev => ({ ...prev, ...favFilters }));
        if (favSearchTerm !== undefined) setSearchTerm(favSearchTerm);
        if (favAllSites !== undefined) setShowAllSites(favAllSites);
      }
    };
    window.addEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
    return () => window.removeEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
  }, []);

  const toggleSelectOne = (e, wonum) => {
    e.stopPropagation();
    setSelectedIds(prev => 
      prev.includes(wonum) ? prev.filter(id => id !== wonum) : [...prev, wonum]
    );
  };

  const handleRowClick = (wonum) => {
    const activeFilters = {
      ...filters,
      searchTerm,
      view: activeView, // 🎯 تمريره للتفاصيل لو محتاجه
      all_sites: showAllSites 
    };

    const cleanFilters = Object.fromEntries(
      Object.entries(activeFilters).filter(([_, value]) => value !== '' && value !== null)
    );

    const queryParams = new URLSearchParams(cleanFilters).toString();
    navigate(`/workorders/${encodeURIComponent(wonum)}${queryParams ? '?' + queryParams : ''}`);
  };

  const filteredWO = useMemo(() => {
    return workOrders.filter(wo => {
        if (!showAllSites) {
          const belongsToUser = wo.site_id === sessionSite && wo.org_id === sessionOrg;
          if (!belongsToUser) return false;
        }
        return true; 
    });
  }, [workOrders, sessionSite, sessionOrg, showAllSites]);

  const sortedData = useMemo(() => {
    let sortableItems = [...workOrders]; 
    
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const valA = String(a[sortConfig.key] || "").toLowerCase();
        const valB = String(b[sortConfig.key] || "").toLowerCase();
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [workOrders, sortConfig]);

  const totalPages = Math.ceil(totalRecords / itemsPerPage) || 1;

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const closePreview = useCallback(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    setHoveredAsset(null);
  }, []);

  const handleMouseEnter = async (e, assetnum) => {
    if (!assetnum) return;
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    
    setMousePos({ x: e.clientX, y: e.clientY });
    try {
      const res = await axios.get(`/api/getassets/${assetnum}`);
      setHoveredAsset(res.data);
    } catch (err) { console.error("Asset info not found"); }
  };

  const handleExport = useCallback(() => {
    // 🎯 الحل السحري: نستخدم الـ State الداخلي بدل الرابط لأن القيم الحقيقية متخزنة هنا
    const currentSiteFilter = filters.site_id;
    const effectiveSite = !showAllSites && !currentSiteFilter ? sessionSite : currentSiteFilter;

    // تجميع كل الفلاتر النشطة (الحقول + البحث السريع + فيو السايدبار)
    let activeFilters = {
        ...filters,
        searchTerm: searchTerm,
        view: activeView, 
        //org_id: sessionOrg,
    };

    if (effectiveSite) {
        activeFilters.site_id = effectiveSite;
    } else {
        delete activeFilters.site_id; 
    }

    // تنظيف الفلاتر الفاضية عشان متبعتش للباك إند
    activeFilters = Object.fromEntries(
        Object.entries(activeFilters).filter(([_, value]) => value !== '' && value !== null)
    );

    const columnsMap = {
        "wonum": t('reports.designer.fields.wonum', 'WO Num'),
        "description": t('wo.fields.description', 'Description'),
        "assetnum": t('wo.fields.asset', 'Asset'),
        "status": t('wo.headers.status', 'Status'),
        "wo_type": t('wo.fields.workType', 'Type'),
        "site_id": t('wo.fields.site', 'Site'),
        "targstartdate": t('wo.fields.schedStart', 'Start Date')
    };

    exportToExcel('workorders', activeFilters, columnsMap, t);
  }, [filters, searchTerm, activeView, showAllSites, sessionSite, sessionOrg, t]);

  useEffect(() => {
    const exportData = () => { handleExport(); };
    window.addEventListener('GLOBAL_EXPORT', exportData);
    return () => window.removeEventListener('GLOBAL_EXPORT', exportData);
  }, [handleExport]);

  // 🎯 الحذف الجماعي - بيكمل على كل أمر شغل لوحده في الباك إند (مش All-or-
  // Nothing)، فبعد التنفيذ بنعرض تقرير: اللي اتحذف فعلاً واللي فشل ولية
  // (معتمد/مكتمل، له تكاليف فعلية، أو له طلب مواد مرتبط)
  const handleBulkDelete = useCallback(() => {
    if (selectedIds.length === 0) {
      Swal.fire({
        ...swalTheme(),
        icon: 'info',
        title: t('common.alert', 'Alert'),
        text: t('wo.messages.bulkDeleteNoSelection'),
        confirmButtonText: t('common.ok', 'OK')
      });
      return;
    }

    Swal.fire({
      ...swalTheme(),
      title: t('common.are_you_sure'),
      text: t('wo.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.delete'),
      cancelButtonText: t('common.cancel'),
      reverseButtons: isRTL,
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await axios.delete('/api/workorders/bulk', {
          headers: { 'Authorization': `Bearer ${token}` },
          data: { wonums: selectedIds }
        });
        const { deleted = [], failed = [] } = res.data || {};

        let html = `<div style="text-align:${isRTL ? 'right' : 'left'}">`;
        if (deleted.length > 0) {
          html += `<p style="color:#16a34a;font-weight:bold;">${t('wo.messages.bulkDeleteSuccess', { count: deleted.length })}</p>`;
        }
        if (failed.length > 0) {
          html += `<p style="font-weight:bold;margin-top:8px;">${t('wo.messages.bulkDeleteFailedListTitle')}:</p>`;
          html += `<div style="max-height:220px;overflow-y:auto;font-size:11px;">`;
          html += failed.map(f => `<div style="padding:4px 0;border-bottom:1px solid #eee;"><b>${f.wonum}</b> — ${f.reason}</div>`).join('');
          html += `</div>`;
        }
        html += `</div>`;

        Swal.fire({
          ...swalTheme(),
          icon: deleted.length > 0 && failed.length === 0 ? 'success' : (deleted.length > 0 ? 'warning' : 'error'),
          title: t('wo.messages.bulkDeleteResultTitle'),
          html,
          confirmButtonText: t('common.ok', 'OK')
        });

        setSelectedIds([]);
        fetchWorkOrders();
      } catch (err) {
        console.error("Bulk Delete Error:", err);
        Swal.fire({
          ...swalTheme(),
          icon: 'error',
          title: t('common.error', 'Error!'),
          text: err.response?.data?.detail || t('wo.messages.fetchError'),
          confirmButtonText: t('common.ok', 'OK')
        });
      }
    });
  }, [selectedIds, t, isRTL, token, fetchWorkOrders]);

  useEffect(() => {
    window.addEventListener('GLOBAL_BULK_DELETE_HANDLER', handleBulkDelete);
    return () => window.removeEventListener('GLOBAL_BULK_DELETE_HANDLER', handleBulkDelete);
  }, [handleBulkDelete]);

  useEffect(() => {
    const handleGlobalClick = (e) => {
      closePreview();
    };

    const handleGlobalMouseMove = (e) => {
      if (hoveredAsset) {
        const dist = Math.sqrt(
          Math.pow(e.clientX - mousePos.x, 2) + 
          Math.pow(e.clientY - mousePos.y, 2)
        );
        if (dist > 350) closePreview();
      }
    };

    window.addEventListener('mousedown', handleGlobalClick);
    window.addEventListener('mousemove', handleGlobalMouseMove);
    
    return () => {
      window.removeEventListener('mousedown', handleGlobalClick);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [hoveredAsset, mousePos, closePreview]);

  return (
    <div className="h-full" style={{ fontSize: `${fontSize}px` }}>
    {hoveredAsset && (
      <div className="fixed pointer-events-none transition-all duration-75"
        style={{ top: mousePos.y + 15, left: mousePos.x + 15, zIndex: 9999 }}>
        <AssetPreviewCard asset={hoveredAsset} />
      </div>
    )}
    <ListPageShell
      icon={<Briefcase size={22} className="text-primary-dark" />}
      title={t('wo.headers.trackingTitle')}
      loading={loading}
      onRefresh={() => fetchWorkOrders()}
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={t('wo.placeholders.searchWO')}
      scopeLabel={showAllSites ? t('wo.headers.globalView') : `${t('wo.headers.site')}: ${sessionSite}`}
      scopeVariant={showAllSites ? 'danger' : 'info'}
      selectedCount={selectedIds.length}
      onClearSelection={() => setSelectedIds([])}
      onClearFilters={handleClearFilters}
      newButtonLabel={t('wo.actions.newWO')}
      onNew={() => navigate('/workorders/new')}
      canCreate={canCreate}
      toolbarExtra={
        <button
          onClick={handleSaveFavoriteFilter}
          className="flex items-center gap-2 px-4 py-2 text-status-warning hover:opacity-70 font-bold transition-colors"
          title={t('favorites.save', 'Save current filters as favorite')}
        >
          <Star size={14} /> {t('favorites.save', 'SAVE AS FAVORITE')}
        </button>
      }
      isRTL={isRTL}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalRecords}
      onPageChange={setCurrentPage}
    >
      <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
        <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-20">
          <tr className={`border-b border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <th className={`p-3 border-x border-white/10 w-10 text-center ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>
              <button onClick={() => setSelectedIds(selectedIds.length === sortedData.length ? [] : sortedData.map(x => x.wonum))}>
                {selectedIds.length === sortedData.length && sortedData.length > 0 ? <CheckSquare size={16}/> : <Square size={16}/>}
              </button>
            </th>
            <SortableTh label={t('reports.designer.fields.wonum')} sortKey="wonum" sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} className="w-48" />
            <SortableTh label={t('wo.fields.description')} sortKey="description" sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} />
            <SortableTh label={t('wo.fields.location')} sortKey="location_id" sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} />
            <SortableTh label={t('wo.fields.asset')} sortKey="assetnum" sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} />
            <SortableTh label={t('wo.headers.status')} sortKey="status" sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} className="w-24" />
            <SortableTh label={t('wo.fields.workType')} sortKey="wo_type" sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} className="w-24" />
            <SortableTh label={t('wo.fields.schedStart')} sortKey="targstartdate" sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} className="w-32" />
            <SortableTh label={t('wo.fields.site')} sortKey="site_id" sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} className="w-32" />
          </tr>
          <tr className={`bg-surface-panel ${isRTL ? 'flex-row-reverse' : ''}`}>
                <td className="border-x"></td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}><input value={filters.wonum} onChange={e => setFilters({...filters, wonum: e.target.value})} className={`w-full border border-line p-1 outline-none font-normal uppercase text-primary ${isRTL ? 'text-right' : 'text-left'}`} /></td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}><input value={filters.description} onChange={e => setFilters({...filters, description: e.target.value})} className={`w-full border border-line p-1 outline-none font-normal text-primary ${isRTL ? 'text-right' : 'text-left'}`} /></td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}><input value={filters.location_id} onChange={e => setFilters({...filters, location_id: e.target.value})} className="w-full border border-line p-1 outline-none uppercase text-center text-primary" /></td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}><input value={filters.assetnum} onChange={e => setFilters({...filters, assetnum: e.target.value})} className="w-full border border-line p-1 outline-none uppercase text-center text-primary" /></td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
                    <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} className={`w-full border border-line p-1 outline-none font-bold uppercase text-[9px] text-primary ${isRTL ? 'text-right' : 'text-left'}`}>
                        <option value="">{t('assets.list.all')}</option>
                        <option value="WAPPR">WAPPR</option>
                        <option value="APPR">APPR</option>
                        <option value="COMP">COMP</option>
                        <option value="CLOSE">CLOSE</option>
                        <option value="INPRG">INPRG</option>
                        <option value="CANC">CANC</option>
                    </select>
                </td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
                    <select value={filters.wo_type} onChange={e => setFilters({...filters, wo_type: e.target.value})} className={`w-full border border-line p-1 outline-none font-bold uppercase text-[9px] text-primary ${isRTL ? 'text-right' : 'text-left'}`}>
                        <option value="">{t('assets.list.all')}</option>
                        <option value="CM">CM</option>
                        <option value="PM">PM</option>
                        <option value="EM">EM</option>
                    </select>
                </td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
                  <DateField value={filters.targstartdate} onChange={val => setFilters({...filters, targstartdate: val})} />
                </td>
                <td className="p-1.5 border-x">
                   <DataField value={filters.site_id} onChange={val => setFilters({...filters, site_id: val})} />
                </td>
              </tr>
            </thead>
            <tbody>
              {loading && workOrders.length === 0 ? (
                <tr><td colSpan="9" className="p-10 text-center animate-pulse text-primary font-bold uppercase tracking-widest">{t('wo.messages.loadingWOs')}</td></tr>
              ) : sortedData.map((wo, i) => (
                <tr key={`${wo.wonum}-${i}`} className={`border-b border-line-subtle hover:bg-primary-soft cursor-pointer group transition-all ${selectedIds.includes(wo.wonum) ? 'bg-primary-soft' : ''} ${isRTL ? 'flex-row-reverse' : ''}`} onClick={() => handleRowClick(wo.wonum)}>
                  <td className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`} onClick={(e) => toggleSelectOne(e, wo.wonum)}>
                    {selectedIds.includes(wo.wonum) ? <CheckSquare size={16} className="text-primary mx-auto"/> : <Square size={16} className="text-ink-secondary mx-auto group-hover:text-primary" />}
                  </td>
                  <td className={`p-3 border-x font-black text-primary uppercase group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>{wo.wonum}</td>
                  <td className={`p-3 border-x text-ink font-semibold ${isRTL ? 'border-l' : 'border-r'}`}>{wo.description}</td>
                  <td className={`p-3 border-x font-bold text-primary text-center uppercase ${isRTL ? 'border-l' : 'border-r'}`}>{wo.location_id || '---'}</td>
                  <td
                    className={`p-3 border-x font-bold text-primary text-center uppercase ${isRTL ? 'border-l' : 'border-r'}`}
                    onMouseEnter={(e) => handleMouseEnter(e, wo.assetnum)}
                    onMouseLeave={() => setHoveredAsset(null)}
                  >
                    <span className="hover:underline hover:text-primary-hover cursor-help">
                      {wo.assetnum || '---'}
                    </span>
                  </td>
                  <td className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                    <StatusBadge status={wo.status} />
                  </td>
                  <td className={`p-3 border-x text-center font-bold text-ink-secondary ${isRTL ? 'border-l' : 'border-r'}`}>{wo.wo_type}</td>
                  <td className={`p-3 border-x text-center font-bold text-ink-secondary ${isRTL ? 'border-l' : 'border-r'}`}>{formatDateTime(wo.targstartdate)}</td>
                  <td className="p-3 text-primary font-black text-center italic opacity-60 uppercase">{wo.site_id || '---'}</td>
                </tr>
              ))}
              {loading && workOrders.length > 0 && (
                 <tr><td colSpan="9" className="p-4 text-center font-black text-primary animate-pulse uppercase">{t('wo.messages.syncing')}</td></tr>
              )}
            </tbody>
          </table>
    </ListPageShell>
    </div>
  );
};

// --- Helper Components ---
const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return dateString.split('.')[0].substring(0, 16).replace('T', ' ');
};
  
const DateField = ({ value, onChange }) => (
  <div className="w-full">
    <input 
      type="datetime-local" 
      className="w-full border border-line p-1 outline-none text-[11px] uppercase font-normal bg-surface-card text-primary text-center"
      value={formatDateTime(value)} 
      onChange={e => onChange(e.target.value)} 
    />
  </div>
);

const DataField = ({ value, onChange }) => (
  <div className="w-full">
    <input 
      className="w-full border border-line p-1 outline-none text-[11px] uppercase font-normal bg-surface-card text-primary text-center font-bold"
      value={value} 
      onChange={e => onChange(e.target.value)} 
    />
  </div>
);

export default WorkOrderList;